import React, { useState, useEffect, useRef, useMemo } from "react";
import "./selectorEERR.scss";
import { GetEERRStructure } from 'src/graphql/newEERR'
import { EERR_AREA_TRANSLATE } from 'src/utils/eerrCategories'

export default function SelectorEERR({
  style = {},
  onChange = () => { },
  onBlur = () => { },
  id = "defaultEERRArea",
  name = "defaultEERRArea",
  className = "",
  placeholder = "Área de EERR",
  defaultValue = "",
  multiple = false,
  clearable = true,
  value = "",
  companyId = "",
  controlled = true,
  showWithoutArea = false,
  showIsHidden = false,
  onOpenChange,
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(controlled ? (value || '') : (defaultValue || ''))
  const [selectedText, setSelectedText] = useState("")
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  
  const OCULT_TEXT = showIsHidden ? 'Ocultos en el Estado de Resultados' : 'Ocultar del Estado de Resultados'

  const { data, fetching, reexecuteQuery } = GetEERRStructure(companyId ? { companyId } : null)

  useEffect(() => {
    if (companyId) {
      reexecuteQuery({ requestPolicy: 'network-only' })
    }
  }, [companyId, reexecuteQuery])

  // Notify parent when dropdown opens/closes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen)
    }
  }, [isOpen, onOpenChange])

  // Update selectedValue when value prop changes (controlled mode)
  useEffect(() => {
    if (controlled && value !== undefined && value !== null) {
      setSelectedValue(value)
    }
  }, [value, controlled])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Build options from EERR structure
  const options = useMemo(() => {
    if (!data?.getCompanyEERRStructure) return []
    
    const eerr = data.getCompanyEERRStructure
    const optionsList = []

    // Add "Ocultar" option first (as in original)
    optionsList.push({
      key: 'none',
      value: 'none',
      text: OCULT_TEXT,
      isSection: false,
      isHidden: true,
    })

    // Add "Sin clasificar" section if needed
    if (showWithoutArea) {
      optionsList.push({
        key: 'section-sinclasificar',
        value: null,
        text: 'SIN CLASIFICAR',
        isSection: true,
      })
      optionsList.push({
        key: 'sinclasificar',
        value: 'sinclasificar',
        text: 'Sin clasificar',
        isSection: false,
      })
    }

    // Build options from EERR report
    Object.keys(eerr.report).forEach((el) => {
      if (el !== "__typename") {
        // Add section header
        optionsList.push({
          key: `section-${el}`,
          value: null,
          text: EERR_AREA_TRANSLATE[el],
          isSection: true,
          category: el,
        })
        
        // Add items for this category
        eerr.report[el].forEach((subEERR) => {
          optionsList.push({
            key: subEERR.fieldKey,
            value: subEERR.fieldKey,
            text: subEERR.field,
            isSection: false,
            category: subEERR.category,
            categoryKey: subEERR.categoryKey,
            type: el,
            eerrData: subEERR,
          })
        })
      }
    })

    return optionsList
  }, [data?.getCompanyEERRStructure, showWithoutArea, OCULT_TEXT])

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options

    const query = searchQuery.toLowerCase()
    return options.filter(opt => {
      if (opt.isSection) {
        // Show section if any item in it matches
        const category = opt.category
        if (category) {
          return options.some(o => 
            !o.isSection && 
            o.type === category && 
            o.text.toLowerCase().includes(query)
          )
        }
        return opt.text.toLowerCase().includes(query)
      }
      return opt.text.toLowerCase().includes(query)
    })
  }, [options, searchQuery])

  // Update selectedText when selectedValue or options change
  useEffect(() => {
    if (selectedValue) {
      if (selectedValue === 'none') {
        setSelectedText(OCULT_TEXT)
      } else {
        const selectedOption = options.find(opt => opt.value === selectedValue)
        if (selectedOption) {
          setSelectedText(selectedOption.text)
        } else {
          setSelectedText('')
        }
      }
    } else {
      setSelectedText('')
    }
  }, [selectedValue, options, OCULT_TEXT])

  // Handle option selection
  const handleSelect = (option) => {
    if (option.isSection) return

    setSelectedValue(option.value)
    setIsOpen(false)
    setSearchQuery('')
    
    if (onChange) {
      // Call onChange with the same signature as Semantic UI
      const event = {}
      const data = {
        value: option.value,
        text: option.text,
        eerrData: option.eerrData,
        options: options, // Include options array for compatibility
      }
      onChange(event, data)
    }
  }

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true)
  }

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchQuery('')
      inputRef.current?.blur()
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault()
      const firstSelectableOption = filteredOptions.find(opt => !opt.isSection)
      if (firstSelectableOption) {
        handleSelect(firstSelectableOption)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
      }
    }
  }

  // Handle clearing selection
  const handleClear = () => {
    setSelectedValue('')
    setSelectedText('')
    setIsOpen(false)
    setSearchQuery('')
    
    if (onChange) {
      onChange({}, { value: '' })
    }
  }

  const displayValue = isOpen ? searchQuery : selectedText
  const showPlaceholder = !selectedText && !isOpen

  if (!companyId || fetching || !data?.getCompanyEERRStructure) {
    return (
      <div className="eerr-selector-dropdown">
        <div className="dropdown-wrapper">
          <input
            type="text"
            className="dropdown-input"
            placeholder={fetching ? 'Cargando...' : 'No se encontraron áreas'}
            disabled
          />
        </div>
      </div>
    )
  }

  return (
    <div 
      ref={dropdownRef}
      className={`eerr-selector-dropdown ${isOpen ? 'open' : ''} ${className}`}
      style={style}
    >
      <div className="dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          className="dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={fetching}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        {selectedValue && clearable && (
          <span 
            className="dropdown-clear"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            title="Limpiar selección"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="16" height="16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l8 8M14 6l-8 8"/>
            </svg>
          </span>
        )}
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
        {fetching && (
          <span className="dropdown-loading">Cargando...</span>
        )}
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? (
            <ul className="dropdown-options" role="listbox">
              {filteredOptions.map((option) => (
                <li
                  key={option.key}
                  className={`dropdown-option ${option.isSection ? 'section-header' : ''} ${selectedValue === option.value ? 'selected' : ''} ${option.isHidden ? 'hidden-option' : ''}`}
                  onClick={() => !option.isSection && handleSelect(option)}
                  role={option.isSection ? 'presentation' : 'option'}
                  aria-selected={option.isSection ? undefined : selectedValue === option.value}
                >
                  {option.isHidden && (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="16" height="16" style={{ marginRight: '8px' }}>
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0110 19.75c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0110 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                    </svg>
                  )}
                  <span className="option-text">{option.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-no-results">
              {searchQuery ? "No se encontraron resultados." : "No hay áreas disponibles."}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
